import { LanguagesEnum } from '@/api/enums';
import { AccountAdminTask } from '@/client/admin-tasks';
import { GroupedOverdueCoursesReport } from '@/client/dashboard/types';
import { FilterNamesEnum } from '@/client/helpers';
import {
  CourseCompletionReportsType,
  DataRange,
  GroupedByEntityFilterType,
  userAwarenessReportsType,
} from '@/client/reports';
import {
  SurveyCategory,
  SurveyCategoryEnum,
} from '@/client/security-culture/types';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import {
  AWT_BOOKING_URL_DK,
  AWT_BOOKING_URL_EN,
  PHISHING_BOOKING_URL_DK,
  PHISHING_BOOKING_URL_EN,
  SC_BOOKING_URL_DK,
  SC_BOOKING_URL_EN,
} from '@/common/constants/external-urls';
import { TranslationFunctionType } from '@/common/types';
import {
  awarenessEmptyImg,
  awarenessSmallImg,
  emptyScDashboard,
  phishingEmptyImg,
  phishingSmallImg,
  securityCultureEmptyImg,
  securityCultureSmallImg,
} from '@/images/dashboard';
import { resolveTranslation } from '@/utils/helpers/admin-tasks.helper';
import {
  colorBlueDark,
  colorGreenChartMiddle,
  colorPurpleText,
} from '@/utils/helpers/css-variables.helper';
import moment from 'moment';

type GetCourseCompletionReportsPropsType = {
  courseCompletionReports: CourseCompletionReportsType;
  t: TranslationFunctionType;
};

export const getCourseCompletionReports = ({
  courseCompletionReports,
  t,
}: GetCourseCompletionReportsPropsType) => {
  return [
    {
      text: t('reports.content.available'),
      count: courseCompletionReports.availableCourses,
      page: {
        to: RedirectPaths[RedirectPathsEnum.COURSES](),
      },
    },
    {
      text: t('reports.content.enrolled'),
      count: courseCompletionReports.enrolledCourses,
      page: {
        to: RedirectPaths[RedirectPathsEnum.ACCOUNT_COURSE_SCHEDULE](),
        filters: {},
      },
    },
    {
      text: t('reports.courses.total.enrollments'),
      count: courseCompletionReports.totalEnrolments,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.SUMMARY_HAVE_ENROLLED_COURSES]: 'yes' },
        isDashboardLink: true,
      },
    },
    {
      text: t('reports.courses.total.completions'),
      count: courseCompletionReports.totalCompletions,
      // TODO: Change by status `completed` and redirect to awareness when BE support this.
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.SUMMARY_HAVE_ENROLLED_COURSES]: 'yes' },
        isDashboardLink: true,
      },
    },
  ];
};

type getUserAwarenessReportsPropsType = {
  userAwarenessReports: userAwarenessReportsType;
  t: TranslationFunctionType;
  isOverdueView?: boolean;
};

export const getUserAwarenessReports = ({
  userAwarenessReports,
  t,
  isOverdueView = false,
}: getUserAwarenessReportsPropsType) => {
  return [
    {
      text: t('reports.users.never.logged.in'),
      count: userAwarenessReports.usersNeverLoggedIn,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.NEVER_LOGGED_IN]: 'yes' },
        isDashboardLink: true,
      },
    },
    {
      text: t('reports.users.overdue.content'),
      count: userAwarenessReports.usersWithOverDueCoursesAndMaterials,
      page: isOverdueView
        ? {
            to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
            filters: { [FilterNamesEnum.SUMMARY_HAVE_OVERDUE_COURSES]: 'yes' },
            isDashboardLink: true,
          }
        : undefined,
      isNA: !isOverdueView,
      tooltip: !isOverdueView
        ? t('reports.users.overdue.content.not.enabled')
        : undefined,
    },
    {
      text: t('reports.users.completed.all.content'),
      count: userAwarenessReports.usersWithCompletedCourses,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.SUMMARY_COMPLETED_ALL_COURSES]: 'yes' },
        isDashboardLink: true,
      },
    },
    {
      text: t('reports.users.not.completed.all.content'),
      count: userAwarenessReports.usersWithoutCompletedCourses,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.SUMMARY_COMPLETED_ALL_COURSES]: 'no' },
        isDashboardLink: true,
      },
    },
  ];
};

export const scormColumnsWidth = {
  THREE: 1696,
  TWO: 1279,
  ONE: 865,
};

export const templatesColumnsWidth = {
  ONE: 1330,
};

export const postersColumnsWidth = {
  THREE: 1950,
  TWO: 1600,
  ONE: 1200,
};

export const SCDemoCategoriesData: SurveyCategory[] = [
  {
    id: 'colleagues-demo',
    name: SurveyCategoryEnum.COLLEAGUES,
    result: 3.0,
    previousResult: 0,
    benchmarkResult: 3.87,
    isLow: false,
  },
  {
    id: 'management-demo',
    name: SurveyCategoryEnum.MANAGEMENT,
    result: 3.7,
    previousResult: 0,
    benchmarkResult: 4.12,
    isLow: false,
  },
  {
    id: 'psychological-safety-demo',
    name: SurveyCategoryEnum.PSYCHOLOGICAL_SAFETY,
    result: 4,
    previousResult: 0,
    benchmarkResult: 4.5,
    isLow: false,
  },
  {
    id: 'resources-demo',
    name: SurveyCategoryEnum.RESOURCES,
    result: 3.1,
    previousResult: 0,
    benchmarkResult: 3.63,
    isLow: false,
  },
  {
    id: 'responsibility-demo',
    name: SurveyCategoryEnum.RESPONSIBILITY,
    result: 3.8,
    previousResult: 0,
    benchmarkResult: 4.15,
    isLow: false,
  },
  {
    id: 'time-demo',
    name: SurveyCategoryEnum.TIME,
    result: 3.1,
    previousResult: 0,
    benchmarkResult: 3.77,
    isLow: false,
  },
];

export const getAdminTaskDetails = (
  adminTask: AccountAdminTask,
  language: LanguagesEnum,
) => {
  const task = adminTask.task;
  const isLink = task.buttonUrl?.startsWith('http');
  const tag = task.tags?.[0];
  const translation = resolveTranslation(task.translations, language);
  const tagTranslation = resolveTranslation(tag?.translations, language);

  return {
    name: translation?.name ?? '',
    label: tagTranslation?.name ?? '',
    tagKey: tag?.key ?? '',
    buttonLabel: translation?.buttonName ?? '',
    redirectUrl: task.buttonUrl ?? '',
    description: translation?.description ?? '',
    isLink,
  };
};

export enum ProductType {
  AWARENESS = 'awareness',
  PHISHING = 'phishing',
  SECURITY_CULTURE = 'security-culture',
  SECURITY_CULTURE_DASHBOARD = 'security-culture-dashboard',
}

export const getEmptyOverviewTranslations = (
  t: TranslationFunctionType,
  type?: ProductType,
) => {
  const docStyle = getComputedStyle(document.documentElement);
  switch (type) {
    case ProductType.AWARENESS:
      return {
        title: t('dashboard.awareness.title'),
        description: t('dashboard.overview.awareness.empty.description'),
        bookingLinkEN: AWT_BOOKING_URL_EN,
        bookingLinkDK: AWT_BOOKING_URL_DK,
        img: awarenessSmallImg,
        color: colorBlueDark(docStyle),
      };
    case ProductType.PHISHING:
      return {
        title: t('dashboard.overview.phishing.empty.title'),
        description: t('dashboard.overview.phishing.empty.description'),
        bookingLinkEN: PHISHING_BOOKING_URL_EN,
        bookingLinkDK: PHISHING_BOOKING_URL_DK,
        img: phishingSmallImg,
        color: colorGreenChartMiddle(docStyle),
      };
    case ProductType.SECURITY_CULTURE:
      return {
        title: t('dashboard.overview.sc.empty.title'),
        description: t('dashboard.overview.sc.empty.description'),
        bookingLinkEN: SC_BOOKING_URL_EN,
        bookingLinkDK: SC_BOOKING_URL_DK,
        img: securityCultureSmallImg,
        color: colorPurpleText(docStyle),
      };
    default:
      return {
        title: t('dashboard.awareness.title'),
        description: t('dashboard.overview.awareness.empty.description'),
        bookingLinkEN: AWT_BOOKING_URL_EN,
        bookingLinkDK: AWT_BOOKING_URL_DK,
        img: awarenessSmallImg,
        color: colorBlueDark(docStyle),
      };
  }
};

export const getEmptyProductCardTranslations = (
  t: TranslationFunctionType,
  type?: ProductType,
) => {
  switch (type) {
    case ProductType.AWARENESS:
      return {
        title: t('dashboard.empty.awareness.title'),
        description: t('dashboard.empty.awareness.description'),
        bookingLinkEN: AWT_BOOKING_URL_EN,
        bookingLinkDK: AWT_BOOKING_URL_DK,
        img: awarenessEmptyImg,
      };
    case ProductType.PHISHING:
      return {
        title: t('dashboard.empty.phishing.title'),
        description: t('dashboard.empty.phishing.description'),
        bookingLinkEN: PHISHING_BOOKING_URL_EN,
        bookingLinkDK: PHISHING_BOOKING_URL_DK,
        img: phishingEmptyImg,
      };
    case ProductType.SECURITY_CULTURE:
      return {
        title: t('dashboard.empty.sc.title'),
        description: t('dashboard.empty.sc.description'),
        bookingLinkEN: SC_BOOKING_URL_EN,
        bookingLinkDK: SC_BOOKING_URL_DK,
        img: securityCultureEmptyImg,
      };
    case ProductType.SECURITY_CULTURE_DASHBOARD:
      return {
        title: t('sc.dashboard.empty.title'),
        description: t('sc.dashboard.empty.description'),
        bookingLinkEN: '',
        bookingLinkDK: '',
        img: emptyScDashboard,
        color: undefined,
      };
    default:
      return {
        title: t('dashboard.empty.awareness.title'),
        description: t('dashboard.empty.awareness.description'),
        bookingLinkEN: AWT_BOOKING_URL_EN,
        bookingLinkDK: AWT_BOOKING_URL_DK,
        img: awarenessEmptyImg,
      };
  }
};

export const dashboardDataRangeOptions = (
  t: TranslationFunctionType,
): DataRange[] => [
  {
    label: t('dashboard.dataRange.last30Days'),
    value: {
      from: moment().subtract(30, 'days').toDate(),
      to: new Date(),
    },
  },
  {
    label: t('dashboard.dataRange.last3Months'),
    value: {
      from: moment()
        .endOf('month')
        .subtract(2, 'months')
        .startOf('month')
        .toDate(),
      to: new Date(),
    },
  },
  {
    label: t('phishing.analytics.dataRange.last12Months'),
    value: {
      from: moment()
        .endOf('month')
        .subtract(11, 'months')
        .startOf('month')
        .toDate(),
      to: new Date(),
    },
  },
  {
    label: t('phishing.analytics.dataRange.allTime'),
    value: {},
  },
];

export const getGroupedOverdueCoursesChartData = (
  data: GroupedOverdueCoursesReport[] | undefined,
) => {
  if (!data) return { dataLabels: [], dataValues: [] };
  let sortedData: GroupedOverdueCoursesReport[] = [];
  let labels: string[] = [];
  let values: number[] = [];

  sortedData = data.sort(
    (a, b) => Number(b.overdueCount) - Number(a.overdueCount),
  );
  labels = sortedData.map((item) => (item.name ? item.name : 'N/A'));
  values = sortedData.map((item) => Number(item.overdueCount));

  return {
    dataLabels: labels,
    dataValues: values,
  };
};

export const getOverdueChartTitle = (
  t: TranslationFunctionType,
  selectedDepartment: GroupedByEntityFilterType,
) => {
  switch (selectedDepartment) {
    case GroupedByEntityFilterType.DEPARTMENT:
      return t('generic.department');
    case GroupedByEntityFilterType.MANAGER:
      return t('generic.manager');
    case GroupedByEntityFilterType.BRANCH:
      return t('branch');
    case GroupedByEntityFilterType.GROUP:
      return t('group');
    default:
      return t('generic.department');
  }
};
